import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, API_BASE, formatApiErrorDetail } from "@/lib/api";
import { Upload, Download, ArrowLeft, FileSpreadsheet, CheckCircle2, AlertTriangle } from "lucide-react";

export default function ImportContracts() {
    const navigate = useNavigate();
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");

    async function downloadTemplate(format) {
        try {
            const token = localStorage.getItem("licito_token");
            const url = `${API_BASE}/contracts/template?format=${format}`;
            const resp = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            if (!resp.ok) throw new Error("Falha ao baixar template.");
            const blob = await resp.blob();
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = format === "csv" ? "template_contratos.csv" : "template_contratos.xlsx";
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (e) {
            setError(e?.message || "Falha ao baixar template.");
        }
    }

    async function doUpload() {
        if (!file) { setError("Selecione um arquivo .xlsx ou .csv."); return; }
        setError(""); setResult(null); setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/contracts/bulk-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setResult(data);
        } catch (e) {
            setError(formatApiErrorDetail(e?.response?.data?.detail) || "Falha no upload");
        } finally {
            setUploading(false);
        }
    }

    return (
        <div className="space-y-6 fade-in max-w-3xl" data-testid="import-contracts-page">
            <header>
                <button onClick={() => navigate(-1)} data-testid="back-button" className="text-xs text-slate-500 hover:text-[#0F2044] inline-flex items-center gap-1">
                    <ArrowLeft className="h-3 w-3" /> Voltar
                </button>
                <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-medium mt-3">Importar em lote</div>
                <h1 className="font-display font-bold text-3xl text-[#0F2044] mt-1">Subir histórico de contratos</h1>
                <p className="text-sm text-slate-500 mt-1">Baixe o template, copie da sua planilha atual e cole nas seções coloridas. Aceitamos .xlsx (recomendado, com 2 abas: <span className="font-semibold">Contratos</span> + <span className="font-semibold">Histórico</span>) e .csv (somente cadastrais).</p>
            </header>

            <section className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
                <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-md bg-emerald-50 text-emerald-700 flex items-center justify-center">
                        <FileSpreadsheet className="h-5 w-5" />
                    </div>
                    <div>
                        <div className="font-display font-semibold text-[#0F2044]">1. Baixe o template</div>
                        <div className="text-xs text-slate-500">XLSX traz 3 abas: <span className="font-semibold">Contratos</span> (com seções coloridas e dropdowns), <span className="font-semibold">Histórico</span> de interações e <span className="font-semibold">Instruções</span>.</div>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button onClick={() => downloadTemplate("xlsx")} data-testid="download-template-xlsx"
                        className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-md bg-[#0F2044] hover:bg-[#172E5E] text-white">
                        <Download className="h-4 w-4" /> Template .xlsx
                    </button>
                    <button onClick={() => downloadTemplate("csv")} data-testid="download-template-csv"
                        className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50">
                        <Download className="h-4 w-4" /> Template .csv
                    </button>
                </div>
            </section>

            <section className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
                <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-md bg-indigo-50 text-indigo-700 flex items-center justify-center">
                        <Upload className="h-5 w-5" />
                    </div>
                    <div>
                        <div className="font-display font-semibold text-[#0F2044]">2. Envie a planilha preenchida</div>
                        <div className="text-xs text-slate-500">Tamanho máximo recomendado: 1 MB.</div>
                    </div>
                </div>
                <label className="block">
                    <input
                        type="file"
                        accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                        onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); setError(""); }}
                        data-testid="file-input"
                        className="block w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                    />
                </label>
                {file && <div className="text-xs text-slate-500">Selecionado: <span className="text-[#0F2044] font-semibold">{file.name}</span> ({Math.round(file.size / 1024)} KB)</div>}
                {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}
                <button onClick={doUpload} disabled={!file || uploading} data-testid="upload-submit-button"
                    className="text-sm px-4 py-2 bg-[#0F2044] hover:bg-[#172E5E] text-white font-semibold rounded-md disabled:opacity-60 inline-flex items-center gap-2">
                    <Upload className="h-4 w-4" /> {uploading ? "Importando…" : "Importar contratos"}
                </button>
            </section>

            {result && (
                <section className="bg-white border border-slate-200 rounded-lg p-6 space-y-3" data-testid="import-result">
                    <div className="flex items-center gap-3">
                        <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                        <h3 className="font-display font-semibold text-[#0F2044]">Resultado da importação</h3>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                        <Stat label="Contratos criados" value={result.criadas || 0} tone="ok" />
                        <Stat label="Notas (histórico)" value={result.historico_notas_criadas || 0} tone={result.historico_notas_criadas > 0 ? "ok" : "muted"} />
                        <Stat label="Reuniões (histórico)" value={result.historico_reunioes_criadas || 0} tone={result.historico_reunioes_criadas > 0 ? "ok" : "muted"} />
                        <Stat label="Linhas exemplo ignoradas" value={result.linhas_exemplo_ignoradas || 0} tone="muted" />
                        <Stat label="Com erro" value={result.erros || 0} tone={result.erros > 0 ? "warn" : "muted"} />
                    </div>
                    {result.erros > 0 && (
                        <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                            <div className="flex items-center gap-2 text-sm font-semibold text-amber-800 mb-2"><AlertTriangle className="h-4 w-4" /> Linhas com erro</div>
                            <ul className="text-xs text-amber-900 space-y-1">
                                {result.detalhe_erros.map((e, i) => (
                                    <li key={i}><span className="font-mono-tab">{e.aba || "Contratos"} · linha {e.linha}:</span> {e.erro}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {result.criadas > 0 && (
                        <Link to="/contratos" className="inline-block text-sm font-semibold text-[#0F2044] hover:underline">Ver contratos →</Link>
                    )}
                </section>
            )}
        </div>
    );
}

function Stat({ label, value, tone }) {
    const colorClass = tone === "ok" ? "text-emerald-700" : tone === "warn" ? "text-amber-700" : "text-slate-700";
    return (
        <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">{label}</div>
            <div className={`font-display font-bold text-2xl ${colorClass}`}>{value}</div>
        </div>
    );
}
